import React, { useEffect, useState } from 'react';
import { useHospitalSelection } from '../../hooks/useHospitalSelection';
import { usePersistedState } from '../../hooks/usePersistedState';
import LineChart from '../../components/Charts/LineChart';
import CustomSelect from '../../components/Common/CustomSelect';
import LoadingNotice from '../../components/LoadingNotice/LoadingNotice';
import { generatePDFIds } from '../../utils/pdfIdHelper';
import './PremCompletion.css';
import html2canvas from "html2canvas"
const PremCompletion: React.FC = () => {
    const { selectedHospitalIds, isMultiSelect, refreshKey } = useHospitalSelection();
    const [chartData6, setChartData6] = useState<any>({ categories: [], series: [] });
    const [chartData7, setChartData7] = useState<any>({ categories: [], series: [] });
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [timePeriod, setTimePeriod] = usePersistedState<string>('prem_completion_timePeriod', 'monthly');
    const [rawPrimaryData, setRawPrimaryData] = useState<any>(null);
    const [rawFallbackData, setRawFallbackData] = useState<any>(null);
    const premCompletionContainerRef = React.useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (selectedHospitalIds.length === 0) {
            setChartData6({ categories: [], series: [] });
            setChartData7({ categories: [], series: [] });
            setRawPrimaryData(null);
            setRawFallbackData(null);
            setInitialLoading(false);
            return;
        }

        setLoading(true);
        const hospitalIdsParam = selectedHospitalIds.join(',');

        const primaryUrl = `/api/prem/${hospitalIdsParam}`;
        const fallbackUrl = `/api/prem_count/${hospitalIdsParam}`;

        Promise.all([
            fetch(primaryUrl).then(res => res.ok ? res.json() : null),
            fetch(fallbackUrl).then(res => res.ok ? res.json() : null)
        ]).then(([primaryData, fallbackData]) => {
            setRawPrimaryData(primaryData);
            setRawFallbackData(fallbackData);
            setLoading(false);
            setInitialLoading(false);
        }).catch(err => {
            console.error("Error fetching PREM data:", err);
            setLoading(false);
            setInitialLoading(false);
        });

    }, [selectedHospitalIds, refreshKey]);

    useEffect(() => {
        if (selectedHospitalIds.length === 0) {
            setChartData6({ categories: [], series: [] });
            setChartData7({ categories: [], series: [] });
            return;
        }

        if (rawPrimaryData === null && rawFallbackData === null) return;

        if (isMultiSelect) {
            processMultiSelectData(rawPrimaryData, rawFallbackData, timePeriod);
        } else {
            processSingleSelectData(rawPrimaryData, rawFallbackData, timePeriod);
        }
    }, [selectedHospitalIds, timePeriod, rawPrimaryData, rawFallbackData, isMultiSelect]);

    const filterRowsByTimePeriod = (rows: any[], period: string, toggleColIndex: number, monthColIndex: number = 2) => {
        const monthToNumber: { [key: string]: number } = {
            Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6,
            Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12
        };

        const normalizeToggle = (v: any) => String(v ?? '').trim().toLowerCase();

        let step1 = rows.filter((row: any) => {
            const toggleValue = normalizeToggle(row[toggleColIndex]);
            switch (period) {
                case 'monthly': return toggleValue === 'monthly';
                case 'quarterly': return toggleValue === 'quarterly';
                case 'bi-yearly': return toggleValue === 'bi-yearly';
                case 'yearly': return toggleValue === 'yearly';
                default: return toggleValue === 'monthly';
            }
        });

        let step2 = step1.filter((row: any) => {
            const monthYear = row[monthColIndex];
            if (period === 'monthly') {
                const [month] = monthYear.split(' ');
                return monthToNumber[month];
            }
            return true;
        });

        let step3 = step2.filter((row: any) => {
            const periodStr = String(row[monthColIndex] || '');
            if (period === 'monthly') {
                const [month, yearStr] = periodStr.split(' ');
                const y = parseInt(yearStr, 10) || 0;
                const m = monthToNumber[month];
                return y > 2025 || (y === 2025 && m >= 9);
            }
            if (period === 'quarterly') {
                const year = parseInt(periodStr.replace(/\D/g, '').slice(-4), 10) || 0;
                const q = parseInt(periodStr.match(/Q(\d)/)?.[1] || '0');
                return year > 2025 || (year === 2025 && q >= 3);
            }
            if (period === 'bi-yearly') {
                const parts = periodStr.split(' ');
                const year = parseInt(parts[parts.length - 1], 10) || 0;
                const half = parts[0];
                return year > 2025 || (year === 2025 && half === 'Jul-Dec');
            }
            if (period === 'yearly') {
                const year = parseInt(periodStr, 10) || 0;
                return year >= 2025;
            }
            return true;
        });

        const result = step3.sort((a: any, b: any) => {
            const monthYearA = a[monthColIndex];
            const monthYearB = b[monthColIndex];

            if (period === 'monthly') {
                const [monthA, yearA] = monthYearA.split(' ');
                const [monthB, yearB] = monthYearB.split(' ');
                const yDiff = (parseInt(yearA, 10) || 0) - (parseInt(yearB, 10) || 0);
                if (yDiff !== 0) return yDiff;
                return monthToNumber[monthA] - monthToNumber[monthB];
            } else if (period === 'quarterly') {
                const yearA = parseInt(monthYearA.replace(/\D/g, '').slice(-4), 10) || 0;
                const yearB = parseInt(monthYearB.replace(/\D/g, '').slice(-4), 10) || 0;
                if (yearA !== yearB) return yearA - yearB;
                const quarterA = parseInt(monthYearA.match(/Q(\d)/)?.[1] || '0');
                const quarterB = parseInt(monthYearB.match(/Q(\d)/)?.[1] || '0');
                return quarterA - quarterB;
            } else if (period === 'bi-yearly') {
                const partsA = monthYearA.split(' ');
                const partsB = monthYearB.split(' ');
                const yearA = parseInt(partsA[partsA.length - 1], 10) || 0;
                const yearB = parseInt(partsB[partsB.length - 1], 10) || 0;
                if (yearA !== yearB) return yearA - yearB;
                const halfA = partsA[0];
                const halfB = partsB[0];
                const isFirstHalfA = halfA === 'Jan-Jun';
                const isFirstHalfB = halfB === 'Jan-Jun';
                return isFirstHalfA === isFirstHalfB ? 0 : (isFirstHalfA ? -1 : 1);
            } else if (period === 'yearly') {
                const yearA = parseInt(monthYearA, 10) || 0;
                const yearB = parseInt(monthYearB, 10) || 0;
                return yearA - yearB;
            }
            return 0;
        });
        return result;
    };

    const processSingleSelectData = (primaryData: any, fallbackData: any, period: string) => {
        const parseValue = (val: any) => {
            if (val === null || val === undefined || val === '') return null;
            const parsed = parseFloat(val);
            return isNaN(parsed) ? null : parsed;
        };

        const toCategoriesAndSeries = (filtered: any[], parseVal: (r: any) => number | null) => ({
            categories: filtered.map((r: any) => r[2]),
            seriesData: filtered.map((r: any) => parseVal(r))
        });

        if (primaryData) {
            const rows = primaryData.response?.result?.rows || [];
            const filtered = filterRowsByTimePeriod(rows, period, 6, 2);
            const { categories, seriesData } = toCategoriesAndSeries(filtered, (r: any) => parseValue(r[5]));
            const rounded = seriesData.map(v => v != null ? Math.round(v * 10) / 10 : null);
            setChartData6({ categories, series: [{ name: 'Total Deliveries %', data: rounded }] });
        } else {
            setChartData6({ categories: [], series: [] });
        }

        if (fallbackData) {
            const rows = fallbackData.response?.result?.rows || [];
            const filtered = filterRowsByTimePeriod(rows, period, 4, 1);

            const periodGroups: { [key: string]: number[] } = {};
            filtered.forEach((row: any) => {
                const periodValue = row[1];
                if (!periodGroups[periodValue]) periodGroups[periodValue] = [];
                const value = parseValue(row[2]);
                if (value !== null) {
                    periodGroups[periodValue].push(value);
                }
            });

            const monthToNumber: { [key: string]: number } = {
                Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6,
                Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12
            };

            const sortedCategories = Object.keys(periodGroups).sort((a, b) => {
                if (period === 'monthly') {
                    const [monthA, yearA] = a.split(' ');
                    const [monthB, yearB] = b.split(' ');
                    const yDiff = (parseInt(yearA, 10) || 0) - (parseInt(yearB, 10) || 0);
                    if (yDiff !== 0) return yDiff;
                    return monthToNumber[monthA] - monthToNumber[monthB];
                }
                return 0;
            });

            const seriesData = sortedCategories.map(period => {
                const values = periodGroups[period];
                return values.reduce((a, b) => a + b, 0);
            });
            setChartData7({ categories: sortedCategories, series: [{ name: 'No. of PREM responses', data: seriesData }] });
        } else if (primaryData) {
            const rows = primaryData.response?.result?.rows || [];
            const filtered = filterRowsByTimePeriod(rows, period, 6, 2);
            const { categories, seriesData } = toCategoriesAndSeries(filtered, (r: any) => parseValue(r[3]));
            setChartData7({ categories, series: [{ name: 'No. of PREM responses', data: seriesData }] });
        } else {
            setChartData7({ categories: [], series: [] });
        }
    };

    const processMultiSelectData = (primaryData: any, fallbackData: any, period: string) => {
        if (primaryData) {
            const rows = primaryData.response?.result?.rows || [];
            const columns = primaryData.response?.result?.column_order || [];
            const filtered = filterRowsByTimePeriod(rows, period, 6, 2);

            const periodGroups: { [key: string]: number[] } = {};
            filtered.forEach((row: any) => {
                const periodValue = row[2];
                if (!periodGroups[periodValue]) periodGroups[periodValue] = [];

                const columnIndex = columns.indexOf('percent') !== -1 ? columns.indexOf('percent') : 5;
                const value = parseFloat(row[columnIndex]);
                if (!isNaN(value)) {
                    periodGroups[periodValue].push(value);
                }
            });

            const sortedPeriods = Object.keys(periodGroups).sort((a, b) => {
                const monthToNumber: { [key: string]: number } = {
                    Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6,
                    Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12
                };

                if (period === 'monthly') {
                    const [monthA, yearA] = a.split(' ');
                    const [monthB, yearB] = b.split(' ');
                    const yDiff = (parseInt(yearA, 10) || 0) - (parseInt(yearB, 10) || 0);
                    if (yDiff !== 0) return yDiff;
                    return monthToNumber[monthA] - monthToNumber[monthB];
                } else if (period === 'quarterly') {
                    const yearA = parseInt(a.replace(/\D/g, '').slice(-4), 10) || 0;
                    const yearB = parseInt(b.replace(/\D/g, '').slice(-4), 10) || 0;
                    if (yearA !== yearB) return yearA - yearB;
                    const qA = parseInt(a.match(/Q(\d)/)?.[1] || '0');
                    const qB = parseInt(b.match(/Q(\d)/)?.[1] || '0');
                    return qA - qB;
                } else if (period === 'bi-yearly') {
                    const partsA = a.split(' ');
                    const partsB = b.split(' ');
                    const yearA = parseInt(partsA[partsA.length - 1], 10) || 0;
                    const yearB = parseInt(partsB[partsB.length - 1], 10) || 0;
                    if (yearA !== yearB) return yearA - yearB;
                    const halfA = partsA[0];
                    const halfB = partsB[0];
                    const isFirstHalfA = halfA === 'Jan-Jun';
                    const isFirstHalfB = halfB === 'Jan-Jun';
                    return isFirstHalfA === isFirstHalfB ? 0 : (isFirstHalfA ? -1 : 1);
                } else if (period === 'yearly') {
                    const yearA = parseInt(a, 10) || 0;
                    const yearB = parseInt(b, 10) || 0;
                    return yearA - yearB;
                }
                return 0;
            });

            const seriesData = sortedPeriods.map(p => {
                const values = periodGroups[p];
                if (!values || values.length === 0) return null;
                const avg = values.reduce((a, b) => a + b, 0) / values.length;
                return Math.round(avg * 10) / 10;
            });

            setChartData6({ categories: sortedPeriods, series: [{ name: 'Total Deliveries %', data: seriesData }] });
        }

        if (fallbackData) {
            const rows = fallbackData.response?.result?.rows || [];
            const columns = fallbackData.response?.result?.column_order || [];
            const filtered = filterRowsByTimePeriod(rows, period, 4, 1);

            const periodGroups: { [key: string]: number[] } = {};
            filtered.forEach((row: any) => {
                const periodValue = row[1];
                if (!periodGroups[periodValue]) periodGroups[periodValue] = [];

                const columnIndex = columns.indexOf('numerator') !== -1 ? columns.indexOf('numerator') : 2;
                const value = parseFloat(row[columnIndex]);
                if (!isNaN(value)) {
                    periodGroups[periodValue].push(value);
                }
            });

            const sortedPeriods = Object.keys(periodGroups).sort((a, b) => {
                const monthToNumber: { [key: string]: number } = {
                    Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6,
                    Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12
                };

                if (period === 'monthly') {
                    const [monthA, yearA] = a.split(' ');
                    const [monthB, yearB] = b.split(' ');
                    const yDiff = (parseInt(yearA, 10) || 0) - (parseInt(yearB, 10) || 0);
                    if (yDiff !== 0) return yDiff;
                    return monthToNumber[monthA] - monthToNumber[monthB];
                } else if (period === 'quarterly') {
                    const yearA = parseInt(a.replace(/\D/g, '').slice(-4), 10) || 0;
                    const yearB = parseInt(b.replace(/\D/g, '').slice(-4), 10) || 0;
                    if (yearA !== yearB) return yearA - yearB;
                    const qA = parseInt(a.match(/Q(\d)/)?.[1] || '0');
                    const qB = parseInt(b.match(/Q(\d)/)?.[1] || '0');
                    return qA - qB;
                } else if (period === 'bi-yearly') {
                    const partsA = a.split(' ');
                    const partsB = b.split(' ');
                    const yearA = parseInt(partsA[partsA.length - 1], 10) || 0;
                    const yearB = parseInt(partsB[partsB.length - 1], 10) || 0;
                    if (yearA !== yearB) return yearA - yearB;
                    const halfA = partsA[0];
                    const halfB = partsB[0];
                    const isFirstHalfA = halfA === 'Jan-Jun';
                    const isFirstHalfB = halfB === 'Jan-Jun';
                    return isFirstHalfA === isFirstHalfB ? 0 : (isFirstHalfA ? -1 : 1);
                } else if (period === 'yearly') {
                    const yearA = parseInt(a, 10) || 0;
                    const yearB = parseInt(b, 10) || 0;
                    return yearA - yearB;
                }
                return 0;
            });

            const seriesData = sortedPeriods.map(p => {
                const values = periodGroups[p];
                if (!values || values.length === 0) return null;
                return values.reduce((a, b) => a + b, 0);
            });

            setChartData7({ categories: sortedPeriods, series: [{ name: 'No. of PREM responses', data: seriesData }] });
        }
    };

    const chart6Ref = React.useRef<any>(null);
    const chart7Ref = React.useRef<any>(null);

    if (initialLoading) {
        return <LoadingNotice title="Loading PREM completion data..." />;
    }

    const closeMenu = (menuId: string) => {
        const menu = document.getElementById(menuId);
        if (menu) menu.style.display = 'none';
    };
   const handleDownloadPNG = async (ref: React.RefObject<HTMLDivElement>, filename: string) => {
        if (ref.current) {
            const canvas = await html2canvas(ref.current, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
                ignoreElements: (el: Element) => el.classList.contains('export-ignore')
            }as any);
            const link = document.createElement('a');
            link.download = `${filename}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        }
    };

    const handleDownloadSVG = async (ref: React.RefObject<HTMLDivElement>, title: string | undefined, filename: string) => {
        if (ref.current) {
            const canvas = await html2canvas(ref.current, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
                ignoreElements: (el: Element) => el.classList.contains('export-menu') || el.classList.contains('export-dropdown') || el.classList.contains("export-ignore2")
            }as any);

            const width = canvas.width / 2;
            const height = canvas.height / 2;
            const dataURL = canvas.toDataURL('image/png');

            let svgTitleElement = '';
            let svgHeight = height;

            if (title) {
                const escapedTitle = title
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/"/g, '&quot;')
                    .replace(/'/g, '&apos;');

                svgTitleElement = `<text x="${width / 2}" y="25" text-anchor="middle" class="chart-title">${escapedTitle}</text>`;
                svgHeight = height + 40;
            }

            const svgContent = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${svgHeight}" viewBox="0 0 ${width} ${svgHeight}">
  <style>
    .chart-title {
      font-family: 'Segoe UI', sans-serif;
      font-size: 16px;
      fill: #1a2a44;
      font-weight: bold;
    }
  </style>
  <rect width="100%" height="100%" fill="white"/>
  ${svgTitleElement}
  <image x="0" y="${title ? 40 : 0}" width="${width}" height="${height}" href="${dataURL}" preserveAspectRatio="xMidYMid meet"/>
</svg>`;

            const blob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${filename}.svg`;
            link.click();
            URL.revokeObjectURL(url);
        }
    };

    const handleExportCSV = (categories: string[], series: any[], filename: string) => {
        const hospitalIds = selectedHospitalIds.join(', ');
        const header = ['Hospital ID(s)', 'Time Period', ...series.map(s => s.name)].join(',');
        const rows = categories.map((cat, i) => {
            const values = series.map(s => s.data[i] ?? '');
            return [`"${hospitalIds}"`, cat, ...values].join(',');
        });
        const csvContent = [header, ...rows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${filename}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };
    return (
        <div id={generatePDFIds.dashboard()} className="prem-completion-page">
            <div className="page-header">
                <h1 className="page-title">Patient Reported Experience Measure (PREM) Completion</h1>
                <div className="controls">
                    <CustomSelect
                        value={timePeriod}
                        onChange={setTimePeriod}
                        options={[
                            { value: 'monthly', label: 'Monthly' },
                            { value: 'quarterly', label: 'Quarterly' },
                            { value: 'bi-yearly', label: 'Bi-Yearly' },
                            { value: 'yearly', label: 'Yearly' }
                        ]}
                    />
                </div>
            </div>

            <div className="chart-section">
                <div id={generatePDFIds.chart(6)} className="chart-container" ref={ premCompletionContainerRef}>
                    <div className="chart-header-row">
                        <h2 className="chart-title">Percentage of Total Deliveries with PREM Completion</h2>
                        <div className="export-menu export-ignore">
                            <button className="hamburger-btn" onClick={() => {
                                const menu = document.getElementById('export-dropdown-menu-prem-1');
                                if (menu) menu.style.display = menu.style.display === 'block' ? 'none' : 'block';
                            }}>&#9776;</button>
                            <div id="export-dropdown-menu-prem-1" className="export-dropdown-menu">
                                <div onClick={() => { handleDownloadSVG(premCompletionContainerRef, undefined, "Percentage of Total Deliveries with PREM Completion"); closeMenu('export-dropdown-menu-prem-1'); }}>Download SVG</div>
                                <div onClick={() => { handleDownloadPNG(premCompletionContainerRef,"Percentage of Total Deliveries with PREM Completion"); closeMenu('export-dropdown-menu-prem-1'); }}>Download PNG</div>
                                <div onClick={() => { handleExportCSV(chartData6.categories, chartData6.series, "Percentage of Total Deliveries with PREM Completion"); closeMenu('export-dropdown-menu-prem-1'); }}>Download CSV</div>
                            </div>
                        </div>
                    </div>
                    <LineChart
                        ref={chart6Ref}
                        categories={chartData6.categories}
                        series={chartData6.series}
                        goalLine={10}
                        goalLabel="Goal ≥ 10%"
                        min={0}
                        max={100}
                        tickAmount={20}
                        markers={false}
                        yAxisLabel="% Total Deliveries"
                        colors={["rgb(86,180,233)"]}
                        fontFamily="Helvetica, Arial, sans-serif"
                        legendPosition="top"
                        legendAlign="right"
                        showXAxisTooltip={true}
                        exportFilename="Percentage of Total Deliveries with PREM Completion"
                        chartTitle="Percentage of Total Deliveries with PREM Completion"
                        pageTitle="Patient Reported Experience Measure (PREM) Completion"
                        isLoading={loading}
                        showZeroForNull={false}
                        treatNullAsZero={false}
                        yAxisDecimals={1}
                        decimals={1}
                        xAxisLabelFontSize="14px"
                    />
                </div>

                <div id={generatePDFIds.chart(7)} className="chart-container">
                    <div className="chart-header-row">
                        <h2 className="chart-title">Total Deliveries with PREM Completion</h2>
                        <div className="export-menu">
                            <button className="hamburger-btn" onClick={() => {
                                const menu = document.getElementById('export-dropdown-menu-prem-2');
                                if (menu) menu.style.display = menu.style.display === 'block' ? 'none' : 'block';
                            }}>&#9776;</button>
                            <div id="export-dropdown-menu-prem-2" className="export-dropdown-menu">
                                <div onClick={() => { chart7Ref.current?.exportToSVG(); closeMenu('export-dropdown-menu-prem-2'); }}>Download SVG</div>
                                <div onClick={() => { chart7Ref.current?.exportToPNG(); closeMenu('export-dropdown-menu-prem-2'); }}>Download PNG</div>
                                <div onClick={() => { handleExportCSV(chartData7.categories, chartData7.series, "Total Deliveries with PREM Completion"); closeMenu('export-dropdown-menu-prem-2'); }}>Download CSV</div>
                            </div>
                        </div>
                    </div>
                    <LineChart
                        ref={chart7Ref}
                        categories={chartData7.categories}
                        series={chartData7.series}
                        goalLine={15}
                        goalLabel="Goal ≥ 15"
                        min={0}
                        max={null as any}
                        markers={false}
                        yAxisLabel="No. of PREM responses"
                        colors={["rgb(86,180,233)"]}
                        fontFamily="Helvetica, Arial, sans-serif"
                        legendPosition="top"
                        legendAlign="right"
                        showXAxisTooltip={true}
                        exportFilename="Total Deliveries with PREM Completion"
                        chartTitle="Total Deliveries with PREM Completion"
                        pageTitle="Patient Reported Experience Measure (PREM) Completion"
                        isLoading={loading}
                        showZeroForNull={false}
                        treatNullAsZero={false}
                        yAxisDecimals={1}
                        decimals={0}
                        xAxisLabelFontSize="14px"
                    />
                </div>
            </div>
        </div>
    );
};

export default PremCompletion;
